import { Instagram, Facebook, Phone, Mail, MapPin, Clock, Heart, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT } from "@/lib/contact";
import { useT } from "@/lib/i18n";

const Footer = () => {
  const { t } = useT();
  return (
    <footer data-testid="site-footer" className="relative bg-slate-900 text-slate-200 overflow-hidden">
      <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-ark-sky/20 blur-3xl" />
      <div className="absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-ark-teal/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-16 pb-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={CONTACT.logoUrl}
                alt="Little Ark Preschool"
                className="h-16 w-auto object-contain bg-white rounded-2xl p-1"
              />
              <div>
                <div className="font-display text-xl font-bold text-white">Little Ark Preschool</div>
                <div className="text-xs uppercase tracking-[0.2em] text-sky-300">Harohalli, Karnataka</div>
              </div>
            </div>
            <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href={CONTACT.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-pink-500/80 flex items-center justify-center transition"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={CONTACT.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-blue-500/80 flex items-center justify-center transition"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display text-base text-white mb-4">{t("footer.visitUs")}</h4>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-ark-teal shrink-0" />
                <span>{CONTACT.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-ark-teal shrink-0" />
                <span>{CONTACT.hours}</span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-display text-base text-white mb-4">{t("footer.admissions")}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-slate-300 hover:text-white"
                >
                  <Phone className="h-4 w-4 text-ark-teal" />
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-slate-300 hover:text-white break-all"
                >
                  <Mail className="h-4 w-4 text-ark-teal" />
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} Little Ark Preschool · Harohalli · {t("footer.rights")}</p>
          <div className="flex items-center gap-5">
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-slate-400 hover:text-white transition flex items-center gap-1.5"
            >
              <LogIn className="h-3.5 w-3.5" />
              {t("footer.adminLogin")}
            </Link>
            <p className="flex items-center gap-2">
              {t("footer.madeWith")} <Heart className="h-3.5 w-3.5 text-pink-400 fill-pink-400" /> {t("footer.forDreamers")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
